import React from 'react';

/**
 * Markdown for assistant replies, rendered straight to React elements.
 *
 * The model answers in Markdown — bold figures, lists, tables of invoices —
 * and the old renderer only special-cased bullet and "Header:" lines, so
 * replies showed raw `**bold**`, `---` and pipe tables. This covers the subset
 * the system prompt asks for: paragraphs, headings, bold / italic / code /
 * strikethrough, nested lists, rules, quotes, code blocks and GitHub tables.
 *
 * Safe by construction: text only ever becomes React text children, which
 * React escapes, so `<img onerror=…>` in a reply shows as text. Links render
 * only for http(s) and mailto URLs; anything else stays plain text.
 */

type Align = 'left' | 'center' | 'right' | undefined;

interface ListItem {
  lines: string[];
  children: Block[];
}

type Block =
  | { type: 'heading'; level: number; text: string }
  | { type: 'paragraph'; lines: string[] }
  | { type: 'list'; ordered: boolean; start: number; items: ListItem[] }
  | { type: 'table'; header: string[]; align: Align[]; rows: string[][] }
  | { type: 'code'; text: string }
  | { type: 'quote'; blocks: Block[] }
  | { type: 'rule' };

const HEADING = /^ {0,3}(#{1,6})\s+(.*?)(?:\s+#+)?\s*$/;
const RULE = /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/;
const FENCE = /^ {0,3}(```|~~~)/;
const QUOTE = /^ {0,3}>\s?(.*)$/;
const LIST_ITEM = /^(\s*)([-*+•]|\d{1,3}[.)])\s+(.*)$/;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

const indentOf = (line: string): number => line.replace(/\t/g, '    ').search(/\S|$/);

const isBlank = (line: string): boolean => line.trim() === '';

// Both lines need a pipe: "Total | 100" followed by a lone "---" is a rule.
const isTableStart = (lines: string[], i: number): boolean =>
  i + 1 < lines.length &&
  lines[i].includes('|') &&
  lines[i + 1].includes('|') &&
  lines[i + 1].includes('-') &&
  TABLE_SEPARATOR.test(lines[i + 1]);

const startsBlock = (lines: string[], i: number): boolean => {
  const line = lines[i];
  return (
    HEADING.test(line) ||
    RULE.test(line) ||
    FENCE.test(line) ||
    QUOTE.test(line) ||
    LIST_ITEM.test(line) ||
    isTableStart(lines, i)
  );
};

/** Split a table row on unescaped pipes, dropping the optional outer pipes. */
const splitRow = (line: string): string[] => {
  let text = line.trim();
  if (text.startsWith('|')) text = text.slice(1);
  if (text.endsWith('|') && !text.endsWith('\\|')) text = text.slice(0, -1);
  const cells: string[] = [];
  let current = '';
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\\' && text[i + 1] === '|') {
      current += '|';
      i++;
    } else if (text[i] === '|') {
      cells.push(current.trim());
      current = '';
    } else {
      current += text[i];
    }
  }
  cells.push(current.trim());
  return cells;
};

const alignOf = (cell: string): Align => {
  const c = cell.trim();
  if (c.startsWith(':') && c.endsWith(':')) return 'center';
  if (c.endsWith(':')) return 'right';
  if (c.startsWith(':')) return 'left';
  return undefined;
};

/** Parse a list starting at `start`; returns the block and the index after it. */
const parseList = (lines: string[], start: number): [Block, number] => {
  const first = LIST_ITEM.exec(lines[start])!;
  const baseIndent = indentOf(lines[start]);
  const ordered = /\d/.test(first[2]);
  const items: ListItem[] = [];
  let i = start;

  while (i < lines.length) {
    const line = lines[i];
    if (isBlank(line)) {
      // A blank line ends the list unless the list carries on after it.
      let next = i + 1;
      while (next < lines.length && isBlank(lines[next])) next++;
      if (next < lines.length && LIST_ITEM.test(lines[next]) && indentOf(lines[next]) >= baseIndent) {
        i = next;
        continue;
      }
      break;
    }
    const match = LIST_ITEM.exec(line);
    const indent = indentOf(line);
    if (match && indent === baseIndent && /\d/.test(match[2]) === ordered) {
      items.push({ lines: [match[3]], children: [] });
      i++;
    } else if (match && indent > baseIndent && items.length) {
      const [child, next] = parseList(lines, i);
      items[items.length - 1].children.push(child);
      i = next;
    } else if (!match && indent > baseIndent && items.length && !startsBlock(lines, i)) {
      // A wrapped continuation of the previous item.
      items[items.length - 1].lines.push(line.trim());
      i++;
    } else {
      break;
    }
  }
  return [{ type: 'list', ordered, start: ordered ? parseInt(first[2], 10) || 1 : 1, items }, i];
};

export const parseBlocks = (markdown: string): Block[] => {
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (isBlank(line)) {
      i++;
      continue;
    }

    if (FENCE.test(line)) {
      const fence = FENCE.exec(line)![1];
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith(fence)) {
        body.push(lines[i]);
        i++;
      }
      i++; // closing fence (or end of text)
      blocks.push({ type: 'code', text: body.join('\n') });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
      i++;
      continue;
    }

    // Before lists: "- - -" and "* * *" are rules, not items.
    if (RULE.test(line)) {
      blocks.push({ type: 'rule' });
      i++;
      continue;
    }

    if (isTableStart(lines, i)) {
      const header = splitRow(line);
      const align = splitRow(lines[i + 1]).map(alignOf);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && !isBlank(lines[i]) && lines[i].includes('|')) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push({ type: 'table', header, align, rows });
      continue;
    }

    if (QUOTE.test(line)) {
      const body: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i])) {
        body.push(QUOTE.exec(lines[i])![1]);
        i++;
      }
      blocks.push({ type: 'quote', blocks: parseBlocks(body.join('\n')) });
      continue;
    }

    if (LIST_ITEM.test(line)) {
      const [list, next] = parseList(lines, i);
      blocks.push(list);
      i = next;
      continue;
    }

    const paragraph: string[] = [];
    while (i < lines.length && !isBlank(lines[i]) && !startsBlock(lines, i)) {
      paragraph.push(lines[i].trim());
      i++;
    }
    blocks.push({ type: 'paragraph', lines: paragraph });
  }
  return blocks;
};

// ---------------------------------------------------------------------------
// Inline
// ---------------------------------------------------------------------------
const ESCAPABLE = '\\`*_{}[]()#+-.!|~>';
const SAFE_HREF = /^(https?:\/\/|mailto:)/i;
const isWordChar = (ch: string | undefined): boolean => !!ch && /[A-Za-z0-9]/.test(ch);
const isSpace = (ch: string | undefined): boolean => ch === undefined || /\s/.test(ch);

/**
 * Index of the delimiter closing an emphasis run opened before `from`, or -1.
 * A closer must follow a non-space character; for `_` it must also not sit
 * inside a word, so snake_case and file_names_like_this stay as written.
 */
const findCloser = (text: string, marker: string, from: number): number => {
  let j = text.indexOf(marker, from);
  while (j !== -1) {
    const before = text[j - 1];
    const after = text[j + marker.length];
    const single = marker.length === 1;
    const partOfLonger = single && (after === marker || before === marker);
    const intraword = marker[0] === '_' && isWordChar(after);
    if (j > from && !isSpace(before) && !partOfLonger && !intraword) return j;
    j = text.indexOf(marker, j + 1);
  }
  return -1;
};

export const renderInline = (text: string, keyPrefix = 'i'): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  let buffer = '';
  let n = 0;
  const key = () => `${keyPrefix}-${n++}`;
  const flush = () => {
    if (buffer) {
      out.push(buffer);
      buffer = '';
    }
  };

  let i = 0;
  while (i < text.length) {
    const ch = text[i];

    if (ch === '\\' && i + 1 < text.length && ESCAPABLE.includes(text[i + 1])) {
      buffer += text[i + 1];
      i += 2;
      continue;
    }

    if (ch === '`') {
      const end = text.indexOf('`', i + 1);
      if (end > i + 1) {
        flush();
        out.push(
          <code key={key()} className="px-1 py-0.5 rounded bg-black/5 dark:bg-white/10 font-mono text-[0.85em]">
            {text.slice(i + 1, end)}
          </code>,
        );
        i = end + 1;
        continue;
      }
    }

    if (ch === '[') {
      const link = /^\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/.exec(text.slice(i));
      if (link) {
        flush();
        const label = renderInline(link[1], `${keyPrefix}-${n}`);
        if (SAFE_HREF.test(link[2])) {
          out.push(
            <a
              key={key()}
              href={link[2]}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 text-coral-700 dark:text-coral-300 hover:text-coral-800 dark:hover:text-coral-200"
            >
              {label}
            </a>,
          );
        } else {
          out.push(<React.Fragment key={key()}>{label}</React.Fragment>);
        }
        i += link[0].length;
        continue;
      }
    }

    const double = text.slice(i, i + 2);
    if (double === '**' || double === '__' || double === '~~') {
      const opens = !isSpace(text[i + 2]) && !(double === '__' && isWordChar(text[i - 1]));
      const end = opens ? findCloser(text, double, i + 2) : -1;
      if (end !== -1) {
        flush();
        const inner = renderInline(text.slice(i + 2, end), `${keyPrefix}-${n}`);
        out.push(
          double === '~~'
            ? <del key={key()}>{inner}</del>
            : <strong key={key()} className="font-semibold">{inner}</strong>,
        );
        i = end + 2;
        continue;
      }
    }

    if ((ch === '*' || ch === '_') && text[i + 1] !== ch) {
      const opens = !isSpace(text[i + 1]) && !(ch === '_' && isWordChar(text[i - 1]));
      const end = opens ? findCloser(text, ch, i + 1) : -1;
      if (end !== -1) {
        flush();
        out.push(<em key={key()}>{renderInline(text.slice(i + 1, end), `${keyPrefix}-${n}`)}</em>);
        i = end + 1;
        continue;
      }
    }

    buffer += ch;
    i++;
  }
  flush();
  return out;
};

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------
const lineBreaks = (lines: string[], keyPrefix: string): React.ReactNode[] =>
  lines.flatMap((line, index) => {
    const nodes = renderInline(line, `${keyPrefix}-${index}`);
    return index === 0 ? nodes : [<br key={`${keyPrefix}-br-${index}`} />, ...nodes];
  });

const renderBlock = (block: Block, key: string): React.ReactNode => {
  switch (block.type) {
    case 'heading': {
      const size = block.level <= 2 ? 'text-base' : 'text-sm';
      const Tag = (`h${Math.min(block.level + 2, 6)}`) as keyof JSX.IntrinsicElements;
      return (
        <Tag key={key} className={`${size} font-semibold mt-3 mb-1 first:mt-0 text-[var(--color-foreground)] dark:text-foreground`}>
          {renderInline(block.text, key)}
        </Tag>
      );
    }
    case 'paragraph':
      return (
        <p key={key} className="my-1.5 first:mt-0 last:mb-0">
          {lineBreaks(block.lines, key)}
        </p>
      );
    case 'list': {
      const items = block.items.map((item, index) => (
        <li key={`${key}-${index}`} className="pl-0.5">
          {lineBreaks(item.lines, `${key}-${index}`)}
          {item.children.map((child, c) => renderBlock(child, `${key}-${index}-${c}`))}
        </li>
      ));
      return block.ordered ? (
        <ol key={key} start={block.start} className="list-decimal pl-5 my-1.5 space-y-0.5 first:mt-0 last:mb-0">
          {items}
        </ol>
      ) : (
        <ul key={key} className="list-disc pl-5 my-1.5 space-y-0.5 first:mt-0 last:mb-0">
          {items}
        </ul>
      );
    }
    case 'table': {
      const width = Math.max(block.header.length, ...block.rows.map((row) => row.length));
      const columns = Array.from({ length: width }, (_, c) => c);
      return (
        <div key={key} className="my-2 first:mt-0 last:mb-0 max-w-full overflow-x-auto rounded-lg border border-[var(--color-border)] dark:border-coral-500/20">
          <table className="w-full border-collapse text-xs">
            <thead className="bg-black/[0.03] dark:bg-white/5">
              <tr>
                {columns.map((c) => (
                  <th
                    key={c}
                    style={{ textAlign: block.align[c] ?? 'left' }}
                    className="px-2.5 py-1.5 font-semibold whitespace-nowrap border-b border-[var(--color-border)] dark:border-coral-500/20"
                  >
                    {renderInline(block.header[c] ?? '', `${key}-h-${c}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r} className="border-b last:border-b-0 border-[var(--color-border)] dark:border-coral-500/10">
                  {columns.map((c) => (
                    <td
                      key={c}
                      style={{ textAlign: block.align[c] ?? 'left' }}
                      className={`px-2.5 py-1.5 align-top ${block.align[c] === 'right' ? 'whitespace-nowrap tabular-nums' : ''}`}
                    >
                      {renderInline(row[c] ?? '', `${key}-${r}-${c}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'code':
      return (
        <pre key={key} className="my-2 first:mt-0 last:mb-0 p-3 rounded-lg bg-black/5 dark:bg-black/30 overflow-x-auto text-xs">
          <code className="font-mono">{block.text}</code>
        </pre>
      );
    case 'quote':
      return (
        <blockquote key={key} className="my-2 first:mt-0 last:mb-0 pl-3 border-l-2 border-coral-300 dark:border-coral-500/40 text-[var(--color-foreground-secondary)] dark:text-foreground-secondary">
          {block.blocks.map((child, c) => renderBlock(child, `${key}-${c}`))}
        </blockquote>
      );
    case 'rule':
      return <hr key={key} className="my-3 border-[var(--color-border)] dark:border-coral-500/20" />;
    default:
      return null;
  }
};

const ChatMarkdown: React.FC<{ content: string }> = ({ content }) => (
  <div className="chat-markdown min-w-0 break-words">
    {parseBlocks(content || '').map((block, index) => renderBlock(block, `b${index}`))}
  </div>
);

export default ChatMarkdown;
